import axios from 'axios';
import { useStore } from '../store/useStore';

let isRefreshing = false;
/** @type {Array<{resolve: (token: string) => void, reject: (err: any) => void}>} */
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL || 'https://api.blishcrown.com',
    headers: { 'Content-Type': 'application/json' }
});

// Request Interceptor: Attach Token (skip if skipAuth is set)
api.interceptors.request.use((config) => {
    if (config.skipAuth) {
        delete config.skipAuth;
        return config;
    }
    const token = useStore.getState().accessToken;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response Interceptor: Handle Errors & Refresh Token
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (!originalRequest || originalRequest.skipAuth) {
            const message = error.response?.data?.message || error.response?.data?.detail || "An unexpected error occurred";
            return Promise.reject({ ...error, message });
        }

        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
            originalRequest.url?.includes('/auth/register') ||
            originalRequest.url?.includes('/auth/verify-email') ||
            originalRequest.url?.includes('/auth/refresh');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    refreshQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }).catch((err) => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refresh = useStore.getState().refreshToken;
                if (!refresh) {
                    isRefreshing = false;
                    processQueue(new Error("No refresh token"));
                    useStore.getState().logout();
                    return Promise.reject(error);
                }
                const { data } = await axios.post(`${api.defaults.baseURL}/auth/refresh`, { refreshToken: refresh });
                useStore.getState().setAuth(useStore.getState().user, data.accessToken, data.refreshToken);
                isRefreshing = false;
                processQueue(null, data.accessToken);
                originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                processQueue(refreshError);
                useStore.getState().logout();
                return Promise.reject(refreshError);
            }
        }

        if (error.response?.status === 403 && error.response.headers['x-kyc-status']) {
            console.warn("KYC Required:", error.response.headers['x-kyc-status']);
        }

        const message = error.response?.data?.message || error.response?.data?.detail || "An unexpected error occurred";
        return Promise.reject({ ...error, message });
    }
);

export default api;
